import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { useExportHistory } from '../../hooks/useExportHistory';
import { exportRefreshEventEmitter } from '../../services/exportRefreshEventEmitter';
import { useTransformExportRecords } from '../../utils/exportDataTransformers';
import ExportPaginationSection from '../exportPaginationSection';
import ExportHistoryHeader from './exportHistoryHeader';
import ExportHistoryTable from './exportHistoryTable';

const ExportHistory = () => {
  const { maintenance_mode } = useSelector((state: RootState) => state.company.company_details);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25; // Hardcoded to 25 rows per page

  // Custom hooks for state management
  const { exportHistory, historyFetchStatus, totalPages, fetchReportHistory } = useExportHistory({
    selectedFilters: [],
    currentPage,
    pageSize
  });

  // Transform data for display
  const transformedRecords = useTransformExportRecords(exportHistory);

  // Page change handlers
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    fetchReportHistory(page, pageSize);
  };

  // Manual refresh
  const handleRefresh = () => {
    fetchReportHistory(currentPage, pageSize, true);
  };

  // Download handler
  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  // Initial data fetch
  useEffect(() => {
    fetchReportHistory(1, pageSize);
  }, []);

  // Register event listener for export refresh
  useEffect(() => {
    const handleEventRefresh = () => {
      fetchReportHistoryRef.current(currentPageRef.current, pageSizeRef.current, true);
    };

    exportRefreshEventEmitter.onRefreshExportHistory(handleEventRefresh);

    return () => {
      exportRefreshEventEmitter.offRefreshExportHistory(handleEventRefresh);
    };
  }, []); // Empty dependency array - no re-subscription needed

  // Use refs to track previous values and avoid dependency issues
  const currentPageRef = useRef(currentPage);
  const pageSizeRef = useRef(pageSize);
  const fetchReportHistoryRef = useRef(fetchReportHistory);

  // Update refs when values change
  useEffect(() => {
    currentPageRef.current = currentPage;
    pageSizeRef.current = pageSize;
    fetchReportHistoryRef.current = fetchReportHistory;
  });

  return (
    <StyledExportHistory maintenance_mode={maintenance_mode.enabled}>
      <ExportHistoryHeader
        onRefresh={handleRefresh}
        isLoading={historyFetchStatus.isLoading}
        totalRecords={historyFetchStatus.data?.total_records}
      />

      <ExportHistoryTable
        records={transformedRecords}
        historyFetchStatus={historyFetchStatus}
        onDownload={handleDownload}
      />

      <ExportPaginationSection
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={handleSetCurrentPage}
        fetchReportHistory={fetchReportHistory}
        pageSize={pageSize}
        historyFetchStatus={historyFetchStatus}
      />
    </StyledExportHistory>
  );
};

export default ExportHistory;

const StyledExportHistory = styled.div<{ maintenance_mode: boolean }>`
  margin-top: ${({ maintenance_mode }) => (maintenance_mode ? '120px' : '60px')};
  padding: 20px 60px 20px 60px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;
